/**
 * AR and AP aging — `/finance/reports/aging`.
 *
 * `currency` is required on both reports and there is deliberately no "all
 * currencies" option: one aging column may only ever hold one currency, and
 * summing two would produce a number that means nothing.
 *
 * The AR report carries a `tie_out` block proving it against the AR control
 * account. The aging report is a subsidiary ledger; when it disagrees with its
 * control account one of the two is wrong. `ties_out: false` is therefore
 * rendered as a loud failure banner — never as a footnote under figures the
 * reader would otherwise take as authoritative.
 *
 * Both reports bucket from the DUE date with an identical vocabulary, because
 * a payable bucket that meant something different from a receivable bucket of
 * the same name would be a reporting trap.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";

import { AGING_BUCKETS, type ARAgingBucketKey, type Money } from "../api";
import { todayIsoDate } from "../decimal";
import {
  DateField,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  TextField,
} from "../components/fields";
import { useAPAgingReport, useARAgingReport, usePartyOptions } from "../queries";

export interface AgingReportsPageProps {
  /** The company's base currency, used as the opening selection. */
  baseCurrency?: string;
  canReadAR?: boolean;
  canReadAP?: boolean;
  canReadParties?: boolean;
}

export function AgingReportsPage({
  baseCurrency = "",
  canReadAR = true,
  canReadAP = true,
  canReadParties = true,
}: AgingReportsPageProps) {
  const { t } = useTranslation();

  const [asOf, setAsOf] = useState(todayIsoDate);
  const [currency, setCurrency] = useState(baseCurrency);
  const [applied, setApplied] = useState({ asOf: todayIsoDate(), currency: baseCurrency });

  const arQuery = useARAgingReport(
    { currency: applied.currency, as_of: applied.asOf },
    canReadAR && applied.currency.length === 3,
  );
  const apQuery = useAPAgingReport(
    { currency: applied.currency, as_of: applied.asOf },
    canReadAP && applied.currency.length === 3,
  );

  const customersQuery = usePartyOptions("customer", canReadParties);
  const suppliersQuery = usePartyOptions("supplier", canReadParties);

  const customerName = useMemo(() => {
    const byId = new Map(
      (customersQuery.data ?? []).map((party) => [party.id, party.display_name]),
    );
    return (id: string): string => byId.get(id) ?? id;
  }, [customersQuery.data]);

  const supplierName = useMemo(() => {
    const byId = new Map(
      (suppliersQuery.data ?? []).map((party) => [party.id, party.display_name]),
    );
    return (id: string): string => byId.get(id) ?? id;
  }, [suppliersQuery.data]);

  const bucketLabel = (bucket: string): string => t(`finance.aging.bucket.${bucket}`, bucket);

  const arBuckets = arQuery.data?.buckets;
  const apTotalsByBucket = useMemo(() => {
    const totals = new Map<string, Money>();
    for (const bucket of apQuery.data?.buckets ?? []) totals.set(bucket.bucket, bucket.amount);
    return totals;
  }, [apQuery.data]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.aging.title", "Aging")}
        description={t(
          "finance.aging.subtitle",
          "Receivables and payables bucketed from the due date, one currency at a time.",
        )}
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <DateField
            id="aging-as-of"
            label={t("finance.aging.asOf", "As of")}
            value={asOf}
            onChange={setAsOf}
            className="w-48"
          />
          <TextField
            id="aging-currency"
            label={t("finance.common.currency", "Currency")}
            value={currency}
            onChange={(value) => setCurrency(value.toUpperCase())}
            hint={t(
              "finance.aging.currencyHint",
              "Required. One column may only ever hold one currency.",
            )}
            className="w-40"
            ltr
            maxLength={3}
          />
          <Button type="button" onClick={() => setApplied({ asOf, currency })}>
            {t("finance.aging.run", "Run the report")}
          </Button>
        </CardContent>
      </Card>

      <Tabs defaultValue="ar">
        <TabsList>
          <TabsTrigger value="ar">{t("finance.aging.tabAR", "Receivables")}</TabsTrigger>
          <TabsTrigger value="ap">{t("finance.aging.tabAP", "Payables")}</TabsTrigger>
        </TabsList>

        <TabsContent value="ar" className="flex flex-col gap-4">
          {!canReadAR ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.aging.arForbidden", "You cannot read the receivables aging report.")}
            </p>
          ) : arQuery.data === undefined ? (
            <p className="text-sm text-muted-foreground">{t("app.loading", "Loading…")}</p>
          ) : (
            <>
              {arQuery.data.tie_out.ties_out ? (
                <NoticeBanner>
                  {t(
                    "finance.aging.tiesOut",
                    "This report ties out to the AR control account at {{balance}}.",
                    { balance: arQuery.data.tie_out.control_balance.amount },
                  )}
                </NoticeBanner>
              ) : (
                <ProblemBanner>
                  {t(
                    "finance.aging.doesNotTieOut",
                    "This report does NOT tie out to the AR control account. The difference is {{difference}} — one of the two is wrong, so do not treat these figures as authoritative.",
                    { difference: arQuery.data.tie_out.difference.amount },
                  )}
                </ProblemBanner>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>
                    <h2>{t("finance.aging.arTotals", "Receivables by bucket")}</h2>
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th scope="col" className="p-2 text-start">
                          {t("finance.aging.customer", "Customer")}
                        </th>
                        {AGING_BUCKETS.map((bucket) => (
                          <th key={bucket} scope="col" className="p-2 text-end">
                            {bucketLabel(bucket)}
                          </th>
                        ))}
                        <th scope="col" className="p-2 text-end">
                          {t("finance.aging.total", "Total")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {arQuery.data.customers.map((customer) => (
                        <tr key={customer.party_id} className="border-b">
                          <td className="p-2">{customerName(customer.party_id)}</td>
                          {AGING_BUCKETS.map((bucket) => (
                            <td key={bucket} className="p-2 text-end">
                              <MoneyDisplay
                                value={customer.buckets[bucket as ARAgingBucketKey]}
                                display="none"
                              />
                            </td>
                          ))}
                          <td className="p-2 text-end font-medium">
                            <MoneyDisplay value={customer.total} display="none" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="p-2 font-medium">{t("finance.aging.total", "Total")}</td>
                        {AGING_BUCKETS.map((bucket) => (
                          <td key={bucket} className="p-2 text-end font-medium">
                            {arBuckets === undefined ? null : (
                              <MoneyDisplay
                                value={arBuckets[bucket as ARAgingBucketKey]}
                                display="none"
                              />
                            )}
                          </td>
                        ))}
                        <td className="p-2 text-end font-medium">
                          <MoneyDisplay value={arQuery.data.total} />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="ap" className="flex flex-col gap-4">
          {!canReadAP ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.aging.apForbidden", "You cannot read the payables aging report.")}
            </p>
          ) : apQuery.data === undefined ? (
            <p className="text-sm text-muted-foreground">{t("app.loading", "Loading…")}</p>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("finance.aging.apTotals", "Payables by bucket")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th scope="col" className="p-2 text-start">
                        {t("finance.aging.supplier", "Supplier")}
                      </th>
                      {AGING_BUCKETS.map((bucket) => (
                        <th key={bucket} scope="col" className="p-2 text-end">
                          {bucketLabel(bucket)}
                        </th>
                      ))}
                      <th scope="col" className="p-2 text-end">
                        {t("finance.aging.total", "Total")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {apQuery.data.suppliers.map((supplier) => {
                      const byBucket = new Map(
                        supplier.buckets.map((bucket) => [bucket.bucket, bucket.amount]),
                      );
                      return (
                        <tr key={supplier.party_id} className="border-b">
                          <td className="p-2">{supplierName(supplier.party_id)}</td>
                          {AGING_BUCKETS.map((bucket) => {
                            const amount = byBucket.get(bucket);
                            return (
                              <td key={bucket} className="p-2 text-end">
                                {amount === undefined ? (
                                  <span className="text-muted-foreground">&mdash;</span>
                                ) : (
                                  <MoneyDisplay value={amount} display="none" />
                                )}
                              </td>
                            );
                          })}
                          <td className="p-2 text-end font-medium">
                            <MoneyDisplay value={supplier.total} display="none" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="p-2 font-medium">{t("finance.aging.total", "Total")}</td>
                      {AGING_BUCKETS.map((bucket) => {
                        const amount = apTotalsByBucket.get(bucket);
                        return (
                          <td key={bucket} className="p-2 text-end font-medium">
                            {amount === undefined ? null : (
                              <MoneyDisplay value={amount} display="none" />
                            )}
                          </td>
                        );
                      })}
                      <td className="p-2 text-end font-medium">
                        <MoneyDisplay value={apQuery.data.total} />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
